import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles, Check, Loader2, CheckCircle2, Lightbulb, ArrowRight } from "lucide-react";
import AppShell from "@/components/AppShell";
import { FullPageLoader, ProvenanceBadge } from "@/components/Common";
import AIDraftDialog from "@/components/AIDraftDialog";
import ValidationTracker from "@/components/ValidationTracker";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function TaskWorkspace() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const resumedRef = useRef(false);
  const [task, setTask] = useState(null);
  const [values, setValues] = useState({});
  const [prov, setProv] = useState({});
  const [status, setStatus] = useState("available");
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const [submitting, setSubmitting] = useState(false);
  const [aiField, setAiField] = useState(null);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [done, setDone] = useState(null); // { assetName, nextSlug, nextTitle }
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get(`/tasks/${slug}`)
      .then(({ data }) => {
        if (!alive) return;
        setTask(data.task);
        setStatus(data.status);
        const src = data.draft?.fields || {};
        const initVals = {};
        data.task.fields.forEach((f) => (initVals[f.key] = src[f.key] || ""));
        setValues(initVals);
        setProv(data.draft?.provenance || {});
      })
      .catch((e) => setError(e.response?.data?.detail || "Could not open this step."));
    return () => { alive = false; };
  }, [slug]);

  // Gentle welcome-back cue when resuming from login.
  useEffect(() => {
    if (task && !resumedRef.current && searchParams.get("resume")) {
      resumedRef.current = true;
      toast("Welcome back", { description: `Let's pick up your ${task.asset_name} where you left off.` });
      const sp = new URLSearchParams(searchParams);
      sp.delete("resume");
      setSearchParams(sp, { replace: true });
    }
  }, [task, searchParams, setSearchParams]);

  const doSave = useCallback(async (vals, provenance) => {
    setSaveState("saving");
    try {
      await api.put(`/tasks/${slug}/draft`, { fields: vals, provenance });
      setSaveState("saved");
    } catch {
      setSaveState("idle");
    }
  }, [slug]);

  const scheduleSave = useCallback((vals, provenance) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => doSave(vals, provenance), 1000);
  }, [doSave]);

  const onChange = (key, value) => {
    const nextVals = { ...values, [key]: value };
    const nextProv = { ...prov, [key]: "student_authored" };
    setValues(nextVals);
    setProv(nextProv);
    setSaveState("saving");
    scheduleSave(nextVals, nextProv);
  };

  const applyAI = (key, text, edited) => {
    const nextVals = { ...values, [key]: text };
    const nextProv = { ...prov, [key]: edited ? "ai_assisted_edited" : "ai_assisted" };
    setValues(nextVals);
    setProv(nextProv);
    doSave(nextVals, nextProv);
  };

  const applyTemplate = (tpl) => {
    const nextVals = { ...values };
    const nextProv = { ...prov };
    let filled = 0;
    task.fields.forEach((f) => {
      if (!(nextVals[f.key] || "").trim() && tpl.fields[f.key]) {
        nextVals[f.key] = tpl.fields[f.key];
        nextProv[f.key] = "student_authored";
        filled += 1;
      }
    });
    setValues(nextVals);
    setProv(nextProv);
    doSave(nextVals, nextProv);
    toast.success(filled ? "Structure added to your empty fields. Now make it yours." : "Every field already has content, nothing to fill.");
  };

  const complete = async () => {
    setSubmitting(true);
    try {
      await api.post(`/tasks/${slug}/complete`, { fields: values, provenance: prov });
      track("Business Asset Created", { task: slug, asset: task.asset_name });
      // Find the recommended next step for continuity.
      let nextSlug = null, nextTitle = null, nextAsset = null;
      try {
        const { data } = await api.get("/dashboard");
        const focus = data.focus;
        if (focus && focus.slug !== slug) { nextSlug = focus.slug; nextTitle = focus.title; nextAsset = focus.asset_name; }
      } catch { /* ignore */ }
      setConfirmOpen(false);
      setDone({ assetName: task.asset_name, nextSlug, nextTitle, nextAsset });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Please complete every field first.");
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  if (error) return (
    <AppShell><div className="mx-auto max-w-3xl px-8 py-16">
      <p className="text-slate-600">{error}</p>
      <button onClick={() => navigate("/dashboard")} className="mt-4 rounded-lg bg-brand px-4 py-2 text-sm text-white">Back to dashboard</button>
    </div></AppShell>
  );
  if (!task) return <FullPageLoader label="Opening the step…" />;

  const allFilled = task.fields.every((f) => (values[f.key] || "").trim());

  return (
    <AppShell>
      <div className="thin-scroll mx-auto max-w-3xl px-8 py-10 ltifade" data-testid="task-workspace">
        <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft size={15} /> Dashboard
        </button>

        <div className="mt-4 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-brand-muted">Step {task.order} of 7 · {task.est_minutes} min</p>
            <h1 className="mt-1.5 font-heading text-2xl font-semibold tracking-tight">{task.title}</h1>
          </div>
          <div className="mt-1 flex items-center gap-1.5 whitespace-nowrap text-xs text-slate-400" data-testid="autosave-indicator">
            {saveState === "saving" && <><Loader2 size={13} className="animate-spin" /> Saving…</>}
            {saveState === "saved" && <><Check size={13} className="text-emerald-500" /> Draft saved</>}
            {saveState === "idle" && "Autosaves as you type"}
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-brand-line bg-brand-surface/40 p-4 text-sm leading-relaxed text-slate-600">
          {task.intro}
        </div>

        {task.why && (
          <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-amber-100 bg-amber-50/60 p-4" data-testid="why-it-matters">
            <Lightbulb size={16} className="mt-0.5 shrink-0 text-amber-500" strokeWidth={2} />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Why this step matters</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">{task.why}</p>
            </div>
          </div>
        )}

        {task.templates?.length > 0 && (
          <div className="mt-5" data-testid="offer-templates">
            <p className="text-sm font-medium text-slate-700">Not sure where to start? Adapt a structure:</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {task.templates.map((tpl) => (
                <button
                  key={tpl.key}
                  data-testid={`template-${tpl.key}`}
                  onClick={() => applyTemplate(tpl)}
                  className="rounded-xl border border-brand-line bg-white p-4 text-left shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-muted hover:shadow-lift"
                >
                  <p className="text-sm font-semibold text-brand-text">{tpl.name}</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{tpl.desc}</p>
                  <span className="mt-2 inline-block text-xs font-medium text-brand">Use this structure →</span>
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-400">Fills empty fields with a starting point in [brackets]. Edit everything to fit your business.</p>
          </div>
        )}

        <div className="mt-6 space-y-6">
          {task.fields.map((f) => (
            <div key={f.key}>
              <div className="mb-1.5 flex items-center justify-between gap-3">
                <label className="text-sm font-medium">{f.label}</label>
                <div className="flex items-center gap-2">
                  <ProvenanceBadge provenance={prov[f.key]} />
                  {f.type !== "choice" && (
                    <button
                      data-testid={`ai-draft-btn-${f.key}`}
                      onClick={() => setAiField(f)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-brand-line px-2.5 py-1 text-xs font-medium text-brand-text transition-all duration-200 hover:bg-brand-surface"
                    >
                      <Sparkles size={13} /> Draft with AI
                    </button>
                  )}
                </div>
              </div>
              {f.hint && <p className="mb-1.5 text-xs text-slate-500" data-testid={`hint-${f.key}`}>{f.hint}</p>}
              {f.type === "choice" ? (
                <div className="flex flex-wrap gap-2" data-testid={`choice-${f.key}`}>
                  {f.options.map((opt) => (
                    <button
                      key={opt}
                      data-testid={`choice-${f.key}-${opt.toLowerCase()}`}
                      onClick={() => onChange(f.key, opt)}
                      className={`rounded-xl border px-5 py-2.5 text-sm font-medium transition-all duration-200 ${
                        values[f.key] === opt ? "border-brand bg-brand-surface text-brand-text" : "border-brand-line bg-white text-slate-600 hover:border-brand-muted"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              ) : (
                <textarea
                  data-testid={`field-${f.key}`}
                  value={values[f.key] || ""}
                  onChange={(e) => onChange(f.key, e.target.value)}
                  rows={f.rows || 3}
                  placeholder={f.placeholder}
                  className="w-full resize-y rounded-xl border border-brand-line bg-white p-3.5 text-sm leading-relaxed shadow-soft focus:border-brand focus:outline-none"
                />
              )}
            </div>
          ))}
        </div>

        {slug === "validate-idea" && <ValidationTracker />}

        <div className="mt-8 flex items-center justify-between border-t border-brand-line pt-6">
          <p className="text-sm text-slate-400">
            {allFilled ? "Looks complete. Save it as your business asset." : "Fill every field to finish this step."}
          </p>
          <button
            data-testid="complete-task-btn"
            onClick={() => setConfirmOpen(true)}
            disabled={!allFilled || submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-3 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover disabled:opacity-40"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            {status === "completed" ? "Review & save changes" : "Review & complete step"}
          </button>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={(o) => !submitting && setConfirmOpen(o)}>
        <AlertDialogContent data-testid="review-approve-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Save your {task.asset_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Take one last look at your answers above. When you approve, this becomes a real business asset
              saved to My Business. You can come back and edit it any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="review-back-btn" disabled={submitting}>Keep editing</AlertDialogCancel>
            <AlertDialogAction data-testid="review-approve-btn" onClick={(e) => { e.preventDefault(); complete(); }} disabled={submitting}>
              {submitting ? <Loader2 size={15} className="mr-1 inline animate-spin" /> : null}
              Approve & save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {done && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" data-testid="task-complete-overlay">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lift ltifade">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-50">
              <CheckCircle2 size={28} className="text-emerald-500" />
            </span>
            <h2 className="mt-5 font-heading text-xl font-semibold">{done.assetName} saved</h2>
            <p className="mt-2 text-sm text-slate-600">
              Nicely done. It's now in My Business, yours to edit or export any time.
            </p>
            <div className="mt-6 flex flex-col gap-2.5">
              {done.nextSlug ? (
                <button
                  data-testid="next-step-btn"
                  onClick={() => { setDone(null); navigate(`/task/${done.nextSlug}`); }}
                  className="group flex items-center justify-between gap-3 rounded-xl bg-brand px-5 py-3.5 text-left text-white transition-all duration-200 hover:bg-brand-hover"
                >
                  <span className="flex flex-col">
                    <span className="text-xs font-medium uppercase tracking-wide text-white/70">Start your next asset</span>
                    <span className="mt-0.5 text-sm font-semibold">{done.nextAsset || done.nextTitle}</span>
                  </span>
                  <ArrowRight size={18} className="shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
                </button>
              ) : (
                <>
                  <button
                    data-testid="go-build-blueprint-btn"
                    onClick={() => navigate("/build")}
                    className="group flex items-center justify-between gap-3 rounded-xl bg-brand px-5 py-3.5 text-left text-white transition-all duration-200 hover:bg-brand-hover"
                  >
                    <span className="flex flex-col">
                      <span className="text-xs font-medium uppercase tracking-wide text-white/70">Your blueprint is complete</span>
                      <span className="mt-0.5 text-sm font-semibold">Generate my Build Blueprint</span>
                    </span>
                    <ArrowRight size={18} className="shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </button>
                  <button
                    data-testid="go-launch-btn"
                    onClick={() => navigate("/launch")}
                    className="rounded-lg border border-brand-line px-6 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  >
                    Or visit the Launch Center
                  </button>
                </>
              )}
              <button
                data-testid="back-dashboard-btn"
                onClick={() => navigate("/dashboard")}
                className="rounded-lg px-6 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-800"
              >
                Back to dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      <AIDraftDialog
        open={!!aiField}
        onOpenChange={(o) => !o && setAiField(null)}
        taskSlug={slug}
        field={aiField}
        existing={aiField ? values[aiField.key] : ""}
        onApply={(text, edited) => applyAI(aiField.key, text, edited)}
      />
    </AppShell>
  );
}
